import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import api from '../services/api'
import AvisoUltimaNota from '../components/AvisoUltimaNota'
import { useEmpresa } from '../contexts/EmpresaContext'
import NovoClienteModal from '../components/NovoClienteModal'
import NaturezaSelect from '../components/NaturezaSelect'
import ItemSelect from '../components/ItemSelect'
import IbsCbsSelect from '../components/IbsCbsSelect'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const UF_IBGE: Record<string, string> = {
  AC:'12',AL:'27',AP:'16',AM:'13',BA:'29',CE:'23',DF:'53',ES:'32',
  GO:'52',MA:'21',MT:'51',MS:'50',MG:'31',PA:'15',PB:'25',PR:'41',
  PE:'26',PI:'22',RJ:'33',RN:'24',RS:'43',RO:'11',RR:'14',SC:'42',
  SP:'35',SE:'28',TO:'17',
}

function calcDV(chave43: string): number {
  const pesos = [2,3,4,5,6,7,8,9]
  let soma = 0
  for (let i = chave43.length - 1, p = 0; i >= 0; i--, p++)
    soma += parseInt(chave43[i]) * pesos[p % 8]
  const resto = soma % 11
  return resto < 2 ? 0 : 11 - resto
}

function gerarChave(empcgc: string, empest: string, dataEmissao: string, serie: string, numero: string, cNF: string): string {
  const cuf  = UF_IBGE[empest.trim()] ?? '43'
  const aamm = dataEmissao.replace(/-/g, '').slice(2, 6)
  const cnpj = empcgc.replace(/\D/g, '').padStart(14, '0')
  const mod  = '55'
  const ser  = serie.padStart(3, '0')
  const num  = numero.padStart(9, '0')
  const chave43 = `${cuf}${aamm}${cnpj}${mod}${ser}${num}1${cNF}`
  return chave43 + calcDV(chave43)
}

function formatDoc(doc: string): string {
  const n = doc.replace(/\D/g, '')
  if (n.length === 14) return n.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  if (n.length === 11) return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  return doc
}

function formatCep(cep: string): string {
  const n = cep.replace(/\D/g, '').padStart(8, '0')
  return n.replace(/(\d{5})(\d{3})/, '$1-$2')
}

const f2 = (n: number) => (n ?? 0).toFixed(2)

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface Empresa { empcod: number; empnom: string; empfan: string; empcgc: string; empest: string }
interface Cliente {
  cli_cod: string; cli_mat: string; cli_nom: string; cli_fan: string
  cli_fj: string; cli_ins: string; cli_est: string; cli_contri: string
  bloqueado?: boolean
  cli_cidade?: string; cli_logradouro?: string; cli_numero?: string; cli_bairro?: string
}
interface Endereco {
  logradouro: string; numero: string; bairro: string; cep: string
  telefone: string; email: string; cidade: string; uf: string
}
interface Natureza {
  tno_cod: number; tno_cdf: number; tno_nat: string; tno_loc: string
  tno_des: string; tno_dsc: string
  tno_tic: string; tno_bic: number; tno_aic: number   // ICMS
  tno_tpi: string; tno_bpi: number; tno_api: number   // PIS
  tno_tcf: string; tno_bcf: number; tno_acf: number   // COFINS
  tno_tin: string; tno_bin: number; tno_ain: number   // IPI
  tno_csa: string
}

interface IbsCbs {
  cst: string; classTrib: string; classTribDes: string
  cbsBc: number; cbsBcEf: number; cbsAliRef: number; cbsRedAli: number; cbsAliEf: number; cbsValor: number
  ibsUfEst: string; ibsUfBcEf: number; ibsUfAliRef: number; ibsUfRedAli: number; ibsUfAliEf: number; ibsUfValor: number
  ibsMunBcEf: number; ibsMunAliRef: number; ibsMunRedAli: number; ibsMunAliEf: number; ibsMunValor: number
  isSbas: number; isSali: number
}

const ibsCbsVazio: IbsCbs = {
  cst: '', classTrib: '', classTribDes: '',
  cbsBc: 0, cbsBcEf: 0, cbsAliRef: 0, cbsRedAli: 0, cbsAliEf: 0, cbsValor: 0,
  ibsUfEst: '', ibsUfBcEf: 0, ibsUfAliRef: 0, ibsUfRedAli: 0, ibsUfAliEf: 0, ibsUfValor: 0,
  ibsMunBcEf: 0, ibsMunAliRef: 0, ibsMunRedAli: 0, ibsMunAliEf: 0, ibsMunValor: 0,
  isSbas: 0, isSali: 0,
}

function calcIbsCbs(total: number, r: any): IbsCbs {
  if (!r || total <= 0) return { ...ibsCbsVazio }
  const r2 = (n: number) => Math.round(n * 100) / 100
  const bcEfC = r2(total * (1 - Number(r.ibs08_crbc) / 100))
  const bcEfU = r2(total * (1 - Number(r.ibs08_urbc) / 100))
  const bcEfM = r2(total * (1 - Number(r.ibs08_mrbc) / 100))
  return {
    cst: r.ibs08_cst?.trim() ?? '',
    classTrib: r.ibs08_tri?.trim() ?? '',
    classTribDes: r.ibs08_d02?.trim() ?? '',
    cbsBc: total, cbsBcEf: bcEfC,
    cbsAliRef: Number(r.ibs08_cali), cbsRedAli: Number(r.ibs08_cpra),
    cbsAliEf: Number(r.ibs08_caef), cbsValor: r2(bcEfC * Number(r.ibs08_caef) / 100),
    ibsUfEst: r.ibs08_uest?.trim() ?? '', ibsUfBcEf: bcEfU,
    ibsUfAliRef: Number(r.ibs08_uali), ibsUfRedAli: Number(r.ibs08_upra),
    ibsUfAliEf: Number(r.ibs08_uaef), ibsUfValor: r2(bcEfU * Number(r.ibs08_uaef) / 100),
    ibsMunBcEf: bcEfM,
    ibsMunAliRef: Number(r.ibs08_mali), ibsMunRedAli: Number(r.ibs08_mpra),
    ibsMunAliEf: Number(r.ibs08_maef), ibsMunValor: r2(bcEfM * Number(r.ibs08_maef) / 100),
    isSbas: Number(r.ibs08_sbas), isSali: Number(r.ibs08_sali),
  }
}

interface ItemNFe {
  cProd: string; descricao: string; unidade: string
  cEAN: string; NCM: string; CEST: string
  complemento: string
  quantidade: number; valorUnitario: number
  // ICMS
  icmsOrig: string; icmsCst: string
  icmsBase: number; icmsAliq: number; icmsDest: number; icmsPRedBC: number
  // ICMS ST
  stBase: number; stAliq: number; stDest: number
  // IPI
  ipiCst: string; ipiBase: number; ipiAliq: number; ipiDest: number
  // PIS / COFINS
  pisCst: string; pisBase: number; pisAliq: number; pisDest: number
  cofinsCst: string; cofinsBase: number; cofinsAliq: number; cofinsDest: number
  vTotTrib: number
  // IBS/CBS
  ibsCbsRegra: string; ibsCbsDes: string
  ibsCbs: IbsCbs
}

interface Parcela { nDup: string; dVenc: string; valor: string }

const ETAPAS = ['Cliente', 'Dados', 'Itens', 'Resumo']

const itemVazio: ItemNFe = {
  cProd:'', descricao:'', unidade:'UN', cEAN:'SEM GTIN', NCM:'', CEST:'',
  complemento:'',
  quantidade:1, valorUnitario:0,
  icmsOrig:'0', icmsCst:'41',
  icmsBase:0, icmsAliq:0, icmsDest:0, icmsPRedBC:0,
  stBase:0, stAliq:0, stDest:0,
  ipiCst:'53', ipiBase:0, ipiAliq:0, ipiDest:0,
  pisCst:'07', pisBase:0, pisAliq:0, pisDest:0,
  cofinsCst:'07', cofinsBase:0, cofinsAliq:0, cofinsDest:0,
  vTotTrib:0,
  ibsCbsRegra:'', ibsCbsDes:'',
  ibsCbs: { ...ibsCbsVazio },
}

function calcImpostosNat(nat: Natureza, vProd: number): Partial<ItemNFe> {
  if (vProd <= 0) return {}
  const r2 = (n: number) => Math.round(n * 100) / 100
  const bc  = (base: number, p: number) => r2(base * (Number(p) / 100))
  const det = (base: number, aliq: number) => r2(base * (Number(aliq) / 100))

  const icmsBase   = bc(vProd, nat.tno_bic);  const icmsDest   = det(icmsBase,   nat.tno_aic)
  const ipiBase    = bc(vProd, nat.tno_bin);  const ipiDest    = det(ipiBase,    nat.tno_ain)
  const pisBase    = bc(vProd, nat.tno_bpi);  const pisDest    = det(pisBase,    nat.tno_api)
  const cofinsBase = bc(vProd, nat.tno_bcf);  const cofinsDest = det(cofinsBase, nat.tno_acf)

  return {
    icmsCst:    nat.tno_tic?.trim() || '41', icmsOrig: '0', icmsPRedBC: 0,
    icmsBase,   icmsAliq:   Number(nat.tno_aic),  icmsDest,
    ipiCst:     nat.tno_tin?.trim() || '53',
    ipiBase,    ipiAliq:    Number(nat.tno_ain),  ipiDest,
    pisCst:     nat.tno_tpi?.trim() || '07',
    pisBase,    pisAliq:    Number(nat.tno_api),  pisDest,
    cofinsCst:  nat.tno_tcf?.trim() || '07',
    cofinsBase, cofinsAliq: Number(nat.tno_acf),  cofinsDest,
  }
}

// ─── Componente ───────────────────────────────────────────────────────────────

export default function EmitirNFeVenda() {
  const { empresaCod: empresaCodGlobal } = useEmpresa()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const editId = searchParams.get('edit')
  const idSalvoRef = useRef<number | null>(null) // nota já gravada nesta tela (reemissão depois de rejeição)
  const copyId = searchParams.get('copyId')

  const [etapa, setEtapa] = useState(0)
  const [empresas, setEmpresas] = useState<Empresa[]>([])
  const [empresa, setEmpresa]   = useState<Empresa | null>(null)
  const [naturezas, setNaturezas] = useState<Natureza[]>([])

  // Step 0 — cliente
  const [busca, setBusca]   = useState('')
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [cliente, setCliente]   = useState<Cliente | null>(null)
  const [buscando, setBuscando] = useState(false)
  const [endereco, setEndereco] = useState<Endereco | null>(null)
  const [editando, setEditando] = useState(false)
  const [endEdit, setEndEdit]   = useState<Endereco>({ logradouro:'', numero:'', bairro:'', cep:'', telefone:'', email:'', cidade:'', uf:'' })
  const [modalNovo, setModalNovo] = useState(false)
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Step 1 — dados
  const [dataEmissao, setDataEmissao] = useState(new Date().toISOString().slice(0, 10))
  const [serie, setSerie]   = useState('')
  const [numero, setNumero] = useState('')
  const [cNF, setCNF]       = useState('')
  const [carregandoNum, setCarregandoNum] = useState(false)
  const [natureza, setNatureza]   = useState('')
  const [tipoOp, setTipoOp]       = useState('VEN')
  const [chaveRef, setChaveRef]   = useState('')
  const [notaRef, setNotaRef]     = useState<any>(null)
  const [notaRefErro, setNotaRefErro] = useState('')

  // Busca a nota referenciada assim que a chave fica completa (tipo DEV).
  useEffect(() => {
    setNotaRef(null); setNotaRefErro('')
    if (tipoOp !== 'DEV' || chaveRef.length !== 44) return
    api.get(`/nfe/referenciada/${chaveRef}`)
      .then(r => setNotaRef(r.data))
      .catch(e => setNotaRefErro(e?.response?.data?.error || 'Não foi possível consultar a nota'))
  }, [tipoOp, chaveRef])
  const [tpNF, setTpNF]           = useState<'0'|'1'>('1')
  const [idDest, setIdDest]       = useState<'1'|'2'|'3'>('1')
  const [indFinal, setIndFinal]   = useState<'0'|'1'>('0')
  const [indPres, setIndPres]     = useState('1')
  const [obs, setObs] = useState('')
  const [chaveNfe, setChaveNfe] = useState('')
  const [errosDados, setErrosDados] = useState<string[]>([])

  // Step 2 — itens
  const [itens, setItens] = useState<ItemNFe[]>([{ ...itemVazio }])
  const [itemExpandido, setItemExpandido] = useState<number | null>(null)
  const [abaFiscal, setAbaFiscal] = useState<Record<number,'impostos'|'ibscbs'>>({})
  const abaItem = (i: number) => abaFiscal[i] ?? 'impostos'

  // Step 3 — pagamento
  const [modFrete] = useState<'0'|'1'|'2'|'9'>('9')
  const [tPag]     = useState('14')
  // Financeiro (mesmo padrão CT-e OS)
  const [financeiroModal, setFinanceiroModal] = useState(false)
  const [finQtdParcelas,  setFinQtdParcelas]  = useState(1)
  const [finIntervalo,    setFinIntervalo]    = useState(0)
  const [finCondDes,      setFinCondDes]      = useState('')
  const [finParcelas,     setFinParcelas]     = useState<{ numero: string; vencimento: string; valor: string }[]>([])
  // Snapshot de finParcelas de antes de abrir o modal — "Cancelar" restaura pra
  // cá, em vez de deixar as parcelas (geradas automaticamente ao abrir "+
  // Financeiro") vazarem pro <cobr> da NF-e mesmo sem o usuário confirmar nada.
  const [finParcelasAntesModal, setFinParcelasAntesModal] = useState<typeof finParcelas>([])
  function fecharFinanceiroSemSalvar() {
    setFinParcelas(finParcelasAntesModal)
    setFinanceiroModal(false)
  }
  const [finContaCod,     setFinContaCod]     = useState('')
  const [finContaNome,    setFinContaNome]    = useState('')
  const [finContaBusca,   setFinContaBusca]   = useState('')
  const [finContaResultados, setFinContaResultados] = useState<{ dep_cod: string; dep_des: string }[]>([])
  const [finContaAberto,  setFinContaAberto]  = useState(false)

  // Resultado
  const [enviando, setEnviando] = useState(false)
  const [resultado, setResultado] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  // ── Carregamento inicial ───────────────────────────────────────────────────
  useEffect(() => {
    api.get('/erp/empresas').then(r => { setEmpresas(r.data); if (r.data.length) setEmpresa(r.data.find((e: Empresa) => e.empcod === empresaCodGlobal) ?? r.data[0]) }).catch(() => {})
    api.get('/erp/naturezas').then(r => setNaturezas(r.data)).catch(() => {})
  }, [])

  // ── Modo edição: carrega payload da nota ──────────────────────────────────
  useEffect(() => {
    if (!editId || !empresas.length || !naturezas.length) return
    api.get(`/nfe/${editId}/payload`).then(r => {
      const fp = r.data
      const emp = empresas.find(e => e.empcod === fp.empcod) ?? empresas[0]
      setEmpresa(emp)
      setCliente(fp.cliente)
      setBusca(fp.cliente?.cli_nom ?? '')
      // O payload guarda a "foto" do cliente de quando a nota foi gerada. Numa
      // reedição de nota rejeitada (ex: IE desatualizada corrigida no cadastro
      // depois), precisa buscar o cliente de novo pra não reenviar o dado velho.
      const cnpjCliente = fp.cliente?.cli_mat?.replace(/\D/g, '')
      if (cnpjCliente) {
        api.get(`/erp/clientes?busca=${cnpjCliente}`).then(r => {
          const atual = r.data.find((c: Cliente) => c.cli_mat?.replace(/\D/g, '') === cnpjCliente)
          if (atual) setCliente(atual)
        }).catch(() => {})
      }
      setEndereco(fp.endereco)
      setEndEdit(fp.endereco ?? { logradouro:'', numero:'', bairro:'', cep:'', telefone:'', email:'', cidade:'', uf:'' })
      setDataEmissao(fp.dataEmissao)
      setSerie(fp.serie)
      setNumero(String(fp.numero))
      setCNF(fp.chave?.slice(35, 43) ?? '')
      setNatureza(String(fp.naturezaCod))
      setTipoOp(fp.tipoOp ?? 'VEN')
      setChaveRef(fp.chaveRef ?? '')
      setTpNF(fp.tpNF ?? '1')
      setIdDest(fp.idDest ?? '1')
      setIndFinal(fp.indFinal ?? '0')
      setIndPres(fp.indPres ?? '1')
      setObs(fp.obs ?? '')
      const itensCarregados = fp.itens ?? [{ ...itemVazio }]
      setItens(itensCarregados)
      // Recalcula o IBS/CBS de cada item com a regra já salva em vez de confiar
      // no valor gravado no payload — uma nota rejeitada por divergência de
      // valor (ex: bug de cálculo já corrigido) tem exatamente o valor errado
      // salvo ali, então reaproveitar ele reproduziria a mesma rejeição.
      itensCarregados.forEach((it: ItemNFe, idx: number) => {
        if (!it.ibsCbsRegra) return
        api.get(`/erp/ibscbs/${it.ibsCbsRegra}`).then(r => {
          if (!r.data) return
          const total = it.quantidade * it.valorUnitario
          const ibsCbs = calcIbsCbs(total, r.data)
          setItens(prev => prev.map((x, i2) => i2 === idx ? { ...x, ibsCbs } : x))
        }).catch(() => {})
      })
      const dups = fp._dadosNFe?.duplicatas ?? []
      if (dups.length) {
        setFinParcelas(dups.map((d: any) => ({ numero: d.nDup, vencimento: d.dVenc, valor: String(d.vDup) })))
      }
      setEtapa(1)
    }).catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, empresas.length, naturezas.length])

  // ── Modo cópia: carrega payload de outra nota, sem chave/série/número ──────
  useEffect(() => {
    if (!copyId || !empresas.length || !naturezas.length) return
    api.get(`/nfe/${copyId}/payload`).then(r => {
      const fp = r.data
      const emp = empresas.find(e => e.empcod === fp.empcod) ?? empresas[0]
      setEmpresa(emp)
      setCliente(fp.cliente)
      setBusca(fp.cliente?.cli_nom ?? '')
      setEndereco(fp.endereco)
      setEndEdit(fp.endereco ?? { logradouro:'', numero:'', bairro:'', cep:'', telefone:'', email:'', cidade:'', uf:'' })
      setDataEmissao(new Date().toISOString().slice(0, 10))
      setNatureza(String(fp.naturezaCod))
      setTipoOp(fp.tipoOp ?? 'VEN')
      setChaveRef(fp.chaveRef ?? '')
      setTpNF(fp.tpNF ?? '1')
      setIdDest(fp.idDest ?? '1')
      setIndFinal(fp.indFinal ?? '0')
      setIndPres(fp.indPres ?? '1')
      setObs(fp.obs ?? '')
      setItens(fp.itens ?? [{ ...itemVazio }])
      setEtapa(1)
    }).catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copyId, empresas.length, naturezas.length])

  // ── Chave automática ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!empresa || !dataEmissao || !serie || !numero || !cNF) return
    setChaveNfe(gerarChave(empresa.empcgc, empresa.empest, dataEmissao, serie, numero, cNF))
  }, [empresa, dataEmissao, serie, numero, cNF])

  // ── Próximo número ao entrar na Step 1 ────────────────────────────────────
  useEffect(() => {
    if (etapa !== 1 || numero || !empresa) return
    setCarregandoNum(true)
    const novoCNF = String(Math.floor(Math.random() * 99999999)).padStart(8, '0')
    setCNF(novoCNF)
    api.get(`/erp/proximonumero/${empresa.empcod}/NFE`)
      .then(r => { setSerie(r.data.serie); setNumero(String(r.data.numero)) })
      .catch(() => setSerie('1'))
      .finally(() => setCarregandoNum(false))
  }, [etapa, empresa?.empcod]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── idDest automático pela UF do cliente ──────────────────────────────────
  useEffect(() => {
    if (!empresa || !cliente) return
    if (!endereco?.uf) return
    setIdDest(endereco.uf.trim().toUpperCase() === empresa.empest.trim().toUpperCase() ? '1' : '2')
  }, [empresa, cliente, endereco])

  // ── Recalcula impostos ao mudar natureza ──────────────────────────────────
  useEffect(() => {
    const nat = naturezas.find(n => String(n.tno_cod) === natureza)
    if (!nat) return
    setItens(prev => prev.map(it => {
      const total = it.quantidade * it.valorUnitario
      return { ...it, ...calcImpostosNat(nat, total) }
    }))
  }, [natureza, naturezas])

  // ── Busca clientes ────────────────────────────────────────────────────────
  function handleBusca(valor: string) {
    setBusca(valor); setCliente(null); setEndereco(null)
    if (debounce.current) clearTimeout(debounce.current)
    if (valor.length < 2) { setClientes([]); return }
    debounce.current = setTimeout(async () => {
      setBuscando(true)
      try { const r = await api.get('/erp/clientes', { params: { busca: valor } }); setClientes(r.data) }
      finally { setBuscando(false) }
    }, 400)
  }

  async function selecionarCliente(c: Cliente) {
    setCliente(c); setBusca(c.cli_nom); setClientes([])
    try {
      const r = await api.get(`/erp/clientes/${c.cli_cod}/endereco`)
      setEndereco(r.data); setEndEdit({ ...r.data })
    } catch {}
    // Sugere o vencimento do financeiro com base na condição de pagamento do cliente
    api.get(`/erp/clientes/${c.cli_cod}/condicao-pagamento`)
      .then(r => {
        setFinIntervalo(r.data?.dias ?? 0)
        setFinCondDes(r.data?.des ?? '')
      })
      .catch(() => { setFinIntervalo(0); setFinCondDes('') })
  }

  // ── Itens ─────────────────────────────────────────────────────────────────
  function addItem() { setItens(prev => [...prev, { ...itemVazio }]) }
  function removeItem(i: number) { setItens(prev => prev.filter((_, idx) => idx !== i)) }

  function updateItem(i: number, campo: keyof ItemNFe, valor: string | number) {
    const nat = naturezas.find(n => String(n.tno_cod) === natureza)
    const itAtual = itens[i]
    setItens(prev => prev.map((it, idx) => {
      if (idx !== i) return it
      const upd = { ...it, [campo]: valor }
      if ((campo === 'quantidade' || campo === 'valorUnitario') && nat) {
        const total = (campo === 'quantidade' ? Number(valor) : it.quantidade)
                    * (campo === 'valorUnitario' ? Number(valor) : it.valorUnitario)
        Object.assign(upd, calcImpostosNat(nat, total))
      }
      return upd
    }))
    // Se a regra IBS/CBS já tinha sido escolhida antes de quantidade/valor
    // estarem preenchidos, o total usado no cálculo original ficava zerado
    // (CST/cClassTrib saíam em branco) — recalcula com o total atualizado.
    if ((campo === 'quantidade' || campo === 'valorUnitario') && itAtual?.ibsCbsRegra) {
      const total = (campo === 'quantidade' ? Number(valor) : itAtual.quantidade)
                  * (campo === 'valorUnitario' ? Number(valor) : itAtual.valorUnitario)
      api.get(`/erp/ibscbs/${itAtual.ibsCbsRegra}`).then(r => {
        if (!r.data) return
        const ibsCbs = calcIbsCbs(total, r.data)
        setItens(prev => prev.map((x, idx2) => idx2 === i ? { ...x, ibsCbs } : x))
      }).catch(() => {})
    }
  }

  const totalGeral = itens.reduce((s, it) => s + it.quantidade * it.valorUnitario, 0)
  const nat = naturezas.find(n => String(n.tno_cod) === natureza)

  // ── Emitir ────────────────────────────────────────────────────────────────
  async function emitir() {
    if (!cliente || !natureza || !numero || !empresa) return
    if (cliente.bloqueado) { setErro('Cliente bloqueado — não é possível emitir documentos para este cliente.'); return }
    setEnviando(true); setErro(null)

    // Recalcula o IBS/CBS de cada item na hora de emitir, em vez de confiar no que
    // já está em estado — se o total do item mudou depois da regra ter sido
    // escolhida (ou qualquer outra corrida entre digitar o valor e clicar na regra),
    // o ibsCbs em memória pode ter ficado com total=0 e CST/cClassTrib em branco
    // (calcIbsCbs zera tudo quando total<=0). Aqui sempre bate com o valor atual.
    const itensCorrigidos = await Promise.all(itens.map(async (it) => {
      if (!it.ibsCbsRegra?.trim()) return it
      const total = it.quantidade * it.valorUnitario
      try {
        const r = await api.get(`/erp/ibscbs/${it.ibsCbsRegra}`)
        if (!r.data) return it
        return { ...it, ibsCbs: calcIbsCbs(total, r.data) }
      } catch {
        return it
      }
    }))

    const indIEDest: '1'|'2'|'9' =
      cliente.cli_ins?.trim() ? '1' :
      cliente.cli_contri === 'S' ? '2' : '9'

    const finNFe: '1'|'2'|'3'|'4' =
      tipoOp === 'CPL' ? '2' :
      tipoOp === 'ANT' ? '3' :
      tipoOp === 'DEV' ? '4' : '1'

    const formPayload = {
      empcod: empresa.empcod, chave: chaveNfe, serie, numero: Number(numero),
      dataEmissao, naturezaCod: natureza, tipoOp, chaveRef, cliente, endereco, finNFe, tpNF, idDest,
      indFinal, indPres, modFrete, tPag, obs, itens: itensCorrigidos,
    }

    const payload = {
      _formPayload: formPayload,
      empcod: empresa.empcod,
      chave: chaveNfe,
      serie,
      numero: Number(numero),
      dataEmissao,
      natureza: { cfop: nat?.tno_cdf ?? 0, descricao: nat?.tno_des ?? '' },
      destinatario: {
        doc: cliente.cli_mat.replace(/\D/g, ''),
        fj: cliente.cli_fj as 'J'|'F',
        nome: cliente.cli_nom?.trim() || cliente.cli_fan,
        ie: cliente.cli_ins?.trim() || '',
        indIEDest,
        endereco: endereco ? {
          logradouro: endereco.logradouro,
          numero: endereco.numero,
          bairro: endereco.bairro,
          cidade: endereco.cidade,
          uf: endereco.uf,
          cep: endereco.cep,
          telefone: endereco.telefone,
          email: endereco.email,
        } : undefined,
      },
      tpNF, idDest, finNFe, indFinal, indPres,
      nfRef: finNFe === '4' ? [chaveRef.replace(/\D/g, '')] : undefined,
      modFrete,
      itens: itensCorrigidos.map(it => ({
        cProd:      it.cProd || '0',
        cEAN:       it.cEAN  || 'SEM GTIN',
        xProd:      it.descricao,
        NCM:        it.NCM.replace(/\D/g, '').padStart(8, '0'),
        CEST:       it.CEST || undefined,
        CFOP:       String(nat?.tno_cdf ?? ''),
        uCom:       it.unidade,
        qCom:       it.quantidade,
        vUnCom:     it.valorUnitario,
        vProd:      it.quantidade * it.valorUnitario,
        indTot:     '1' as '1',
        infAdProd:  it.complemento?.trim() || undefined,
        impostos: {
          icmsOrig:        it.icmsOrig,
          icmsCst:         it.icmsCst,
          icmsBase:        it.icmsBase,
          icmsAliq:        it.icmsAliq,
          icmsDest:        it.icmsDest,
          icmsPRedBC:      it.icmsPRedBC,
          ipiCst:          it.ipiCst,
          ipiBase:         it.ipiBase,
          ipiAliq:         it.ipiAliq,
          ipiDest:         it.ipiDest,
          pisCst:          it.pisCst,
          pisBase:         it.pisBase,
          pisAliq:         it.pisAliq,
          pisDest:         it.pisDest,
          cofinsCst:       it.cofinsCst,
          cofinsBase:      it.cofinsBase,
          cofinsAliq:      it.cofinsAliq,
          cofinsDest:      it.cofinsDest,
          vTotTrib:        it.vTotTrib,
          stBase:          it.stBase    || undefined,
          stAliq:          it.stAliq    || undefined,
          stDest:          it.stDest    || undefined,
          ibsCbsCst:       it.ibsCbs.cst?.trim()       || undefined,
          ibsCbsClassTrib: it.ibsCbs.classTrib?.trim() || undefined,
          ibsUfAliEf:      it.ibsCbs.ibsUfAliEf  || undefined,
          ibsUfValor:      it.ibsCbs.ibsUfValor   || undefined,
          ibsMunAliEf:     it.ibsCbs.ibsMunAliEf  || undefined,
          ibsMunValor:     it.ibsCbs.ibsMunValor   || undefined,
          cbsAliEf:        it.ibsCbs.cbsAliEf     || undefined,
          cbsValor:        it.ibsCbs.cbsValor      || undefined,
        },
      })),
      duplicatas: finParcelas.length > 0
        ? finParcelas
            .map(p => ({ nDup: p.numero, dVenc: p.vencimento, vDup: parseFloat(p.valor) || 0 }))
            .filter(d => d.vDup > 0)
        : undefined,
      tPag,
      vPag: totalGeral,
      obs,
    }

    try {
      let id: number
      if (editId) {
        await api.put(`/nfe/${editId}`, payload)
        id = Number(editId)
      } else {
        const rs = await api.post('/nfe/salvar', { ...payload, _idSalvo: idSalvoRef.current ?? undefined })
        id = rs.data.id
        idSalvoRef.current = id
      }

      let autorizado = false
      try {
        const rt = await api.post(`/nfe/${id}/transmitir`)
        const ret = rt.data
        if (ret.autorizado) {
          autorizado = true
          setResultado(`✓ Autorizado — Protocolo ${ret.protocolo}`)
        } else if (ret.cStat === '204') {
          setResultado(`cStat 204: Nota já transmitida anteriormente.\n\nUse "Consultar SEFAZ" na lista de transmissão para recuperar o protocolo.`)
        } else {
          setResultado(`REJEITADO\n\ncStat ${ret.cStat}: ${ret.xMotivo}`)
        }
      } catch (et: any) {
        const detalhe = et?.response?.data?.error || et?.message || 'Erro desconhecido'
        try {
          const rst = await api.get(`/nfe/${id}/status`)
          if (rst.data.situacao === 'AUTORIZADA') {
            autorizado = true
            setResultado(`✓ Autorizado — Protocolo ${rst.data.protocolo}`)
          } else {
            setResultado(`ERRO AO TRANSMITIR\n\n${detalhe}`)
          }
        } catch {
          setResultado(`ERRO AO TRANSMITIR\n\n${detalhe}`)
        }
      }

      if (autorizado && finContaCod.trim() && finParcelas.length > 0) {
        try {
          await api.post(`/nfe/${id}/financeiro`, { parcelas: finParcelas, contaCod: finContaCod })
        } catch (ef: any) {
          setResultado(prev => `${prev}\n\nATENÇÃO: falha ao vincular financeiro — ${ef?.response?.data?.error || ef?.message || 'erro desconhecido'}`)
        }
      }

      if (autorizado) setTimeout(() => navigate('/nfe/lista'), 2200)
    } catch (e: any) {
      if (e?.response?.data?.codigo === 'NUMERO_EM_USO') setNumero(String(e.response.data.proximo))
      setErro(e?.response?.data?.error || 'Erro ao emitir.')
    } finally {
      setEnviando(false)
      setEtapa(4)
    }
  }

  function reiniciar() {
    setEtapa(0); setBusca(''); setCliente(null); setClientes([]); setEndereco(null)
    setNatureza(''); setSerie(''); setNumero(''); setObs('')
    idSalvoRef.current = null
    setItens([{ ...itemVazio }]); setFinParcelas([])
    setResultado(null); setErro(null)
  }

  // ─── Tela: transmitindo ────────────────────────────────────────────────────
  if (etapa === 4 && enviando) {
    return (
      <div className="card" style={{ textAlign:'center', padding:'3rem' }}>
        <div style={{ fontSize:'2.5rem', marginBottom:'1rem' }}>⏳</div>
        <h2 style={{ color:'#374151', marginBottom:'0.5rem' }}>Transmitindo para o SEFAZ...</h2>
        <p style={{ color:'#6b7280', fontSize:'0.9rem' }}>Aguarde, isso pode levar alguns segundos.</p>
      </div>
    )
  }

  // ─── Tela: resultado ───────────────────────────────────────────────────────
  if (etapa === 4 && resultado !== null) {
    const isOk  = resultado.startsWith('✓')
    const isRej = resultado.startsWith('REJEITADO')
    return (
      <div className="card" style={{ textAlign:'center', padding:'3rem' }}>
        <div style={{ fontSize:'3rem', marginBottom:'1rem' }}>{isOk ? '✅' : '❌'}</div>
        <h2 style={{ color: isOk ? '#065f46' : '#dc2626', marginBottom:'0.75rem' }}>
          {isOk ? 'Autorizado pelo SEFAZ!' : isRej ? 'Nota Rejeitada' : 'Erro na Transmissão'}
        </h2>
        <div style={{
          background: isOk ? '#ecfdf5' : '#fef2f2',
          border: `1px solid ${isOk ? '#6ee7b7' : '#fca5a5'}`,
          borderRadius:'0.5rem', padding:'1rem 1.5rem', marginBottom:'1.5rem',
          display:'inline-block', textAlign:'left', maxWidth:480,
          whiteSpace:'pre-line', fontFamily:'monospace', fontSize:'0.85rem',
          color: isOk ? '#065f46' : '#7f1d1d', wordBreak:'break-word',
        }}>{resultado}</div>
        {isOk && <p style={{ color:'#6b7280', fontSize:'0.9rem', marginBottom:'1rem' }}>Redirecionando para a lista...</p>}
        <div style={{ display:'flex', gap:'0.75rem', justifyContent:'center', flexWrap:'wrap' }}>
          <button className="btn btn-primary" onClick={() => navigate('/nfe/lista')}>Ir para a Lista</button>
          <button className="btn btn-secondary" onClick={reiniciar}>Nova Emissão</button>
        </div>
      </div>
    )
  }

  if (etapa === 4 && erro) {
    return (
      <div className="card" style={{ textAlign:'center', padding:'3rem' }}>
        <div style={{ fontSize:'3rem', marginBottom:'1rem' }}>❌</div>
        <h2 style={{ color:'#dc2626', marginBottom:'0.75rem' }}>Erro ao Emitir</h2>
        <div style={{ background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:'0.5rem', padding:'1rem', marginBottom:'1.5rem', maxWidth:480, margin:'0 auto 1.5rem', fontFamily:'monospace', fontSize:'0.85rem', color:'#7f1d1d' }}>
          {erro}
        </div>
        <button className="btn btn-secondary" onClick={() => { setEtapa(3); setErro(null) }}>← Voltar</button>
      </div>
    )
  }

  // ─── Form principal ────────────────────────────────────────────────────────
  return (
    <>
      <AvisoUltimaNota empcod={empresa?.empcod ?? 1} modelo="55" serie={serie} proximo={numero} onNumeroAjustado={n => setNumero(String(n))} />
      {/* Stepper */}
      <div className="stepper">
        {ETAPAS.map((e, i) => (
          <div key={e} className={`step ${i === etapa ? 'step--active' : i < etapa ? 'step--done' : ''}`}>
            <div className="step-circle">{i < etapa ? '✓' : i + 1}</div>
            <span>{e}</span>
          </div>
        ))}
      </div>

      <div className="card">

        {/* ── Etapa 0: Cliente ─────────────────────────────────────────────── */}
        {etapa === 0 && (
          <div>
            <h3 className="etapa-titulo">Destinatário</h3>

            {!cliente && (
              <div className="busca-cliente-wrap">
                <div className="field" style={{ marginBottom:0 }}>
                  <label>Nome, fantasia ou CNPJ/CPF</label>
                  <div className="busca-cliente-row">
                    <input type="text" placeholder="Digite para buscar..." value={busca}
                      onChange={e => handleBusca(e.target.value)} autoFocus />
                    {buscando && <span className="busca-spinner">Buscando...</span>}
                    <button className="btn btn-primary btn-sm" onClick={() => setModalNovo(true)} style={{ whiteSpace:'nowrap' }}>
                      + Novo
                    </button>
                  </div>
                </div>
              </div>
            )}

            {modalNovo && (
              <NovoClienteModal onClose={() => setModalNovo(false)} onSalvo={c => { setModalNovo(false); selecionarCliente(c) }} />
            )}

            {clientes.length > 0 && !cliente && (
              <div className="lista-clientes">
                {clientes.map(c => (
                  <button key={c.cli_cod} className="item-cliente" onClick={() => selecionarCliente(c)}>
                    <div className="item-cliente-linha1">
                      <strong>{c.cli_nom}</strong>
                      <span className="badge-tipo">{c.cli_fj === 'J' ? 'PJ' : 'PF'}</span>
                    </div>
                    {c.cli_fan && c.cli_fan !== c.cli_nom && <span className="item-cliente-fantasia">{c.cli_fan}</span>}
                    <div className="item-cliente-doc">
                      <span>{c.cli_fj === 'J' ? 'CNPJ' : 'CPF'}: {formatDoc(c.cli_mat)}</span>
                      {c.cli_est?.trim() && <span>UF: {c.cli_est}</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {cliente && (
              <div className="cliente-card">
                <div className="cliente-card-header">
                  <div>
                    <div className="cliente-card-nome">{cliente.cli_nom}</div>
                    {cliente.cli_fan && cliente.cli_fan !== cliente.cli_nom && (
                      <div className="cliente-card-fantasia">{cliente.cli_fan}</div>
                    )}
                  </div>
                </div>
                <div className="cliente-card-fiscal">
                  <span><b>{cliente.cli_fj === 'J' ? 'CNPJ' : 'CPF'}:</b> {formatDoc(cliente.cli_mat)}</span>
                  {cliente.cli_ins?.trim() && <span><b>IE:</b> {cliente.cli_ins}</span>}
                  <span><b>Contribuinte:</b> {cliente.cli_ins?.trim() || cliente.cli_contri !== 'N' ? 'Sim' : 'Não'}</span>
                </div>
                {cliente.bloqueado && (
                  <div style={{ background: '#fee2e2', color: '#991b1b', fontWeight: 700, fontSize: '0.85rem', padding: '0.5rem 0.75rem', borderRadius: 4, margin: '0.5rem 0' }}>
                    ⚠ CLIENTE BLOQUEADO — não é possível emitir documentos para este cliente.
                  </div>
                )}
                {!editando && endereco && (
                  <div className="cliente-card-end">
                    {(endereco.logradouro?.trim() || endereco.numero?.trim()) && (
                      <div className="end-linha">
                        <span className="end-icone">📍</span>
                        <span>{[endereco.logradouro, endereco.numero, endereco.bairro].filter(Boolean).join(', ')}{endereco.cidade ? ` — ${endereco.cidade}/${endereco.uf}` : ''}{endereco.cep?.trim() ? ` · CEP ${formatCep(endereco.cep)}` : ''}</span>
                      </div>
                    )}
                  </div>
                )}
                {editando && (
                  <div className="cliente-card-edit">
                    <div className="form-grid-3">
                      {([['Logradouro','logradouro'],['Número','numero'],['Bairro','bairro'],['Cidade','cidade'],['UF','uf'],['CEP','cep'],['Telefone','telefone'],['E-mail','email']] as [string,keyof Endereco][]).map(([label, key]) => (
                        <div key={key} className="field" style={key==='email' ? {gridColumn:'span 2'} : {}}>
                          <label>{label}</label>
                          <input value={endEdit[key]} onChange={e => setEndEdit(p => ({ ...p, [key]: e.target.value }))} />
                        </div>
                      ))}
                    </div>
                    <div style={{ display:'flex', justifyContent:'flex-end', marginTop:'0.75rem' }}>
                      <button className="btn btn-primary btn-sm" onClick={() => { setEndereco({ ...endEdit }); setEditando(false) }}>Confirmar</button>
                    </div>
                  </div>
                )}
                <div className="cliente-card-footer">
                  <button className="btn-editar" onClick={() => { setEditando(e => !e); setEndEdit(endereco ?? endEdit) }}>
                    {editando ? 'Cancelar' : 'Editar'}
                  </button>
                  <button className="btn-trocar-cliente" onClick={() => { setCliente(null); setBusca(''); setClientes([]); setEndereco(null) }}>
                    Trocar
                  </button>
                  <span style={{ width:'1px', height:'18px', background:'#d1fae5', display:'inline-block', margin:'0 0.25rem' }} />
                  <button className="btn btn-primary btn-sm" disabled={cliente.bloqueado} onClick={() => setEtapa(1)}>Próximo →</button>
                </div>
              </div>
            )}

            {!cliente && (
              <div className="etapa-acoes">
                <button className="btn btn-primary" disabled>Próximo →</button>
              </div>
            )}
          </div>
        )}

        {/* ── Etapa 1: Dados ───────────────────────────────────────────────── */}
        {etapa === 1 && (
          <div>
            <h3 className="etapa-titulo">Dados do Documento</h3>

            {/* Cabeçalho: empresa, data, série, número */}
            <div style={{ display:'flex', gap:'1rem', alignItems:'flex-end', background:'#1a6ea8', borderRadius:'0.5rem', padding:'0.65rem 1rem', marginBottom:'0.85rem' }}>
              <div className="field" style={{ flex:'0 0 260px', marginBottom:0 }}>
                <label style={{ color:'#bfdbfe', fontWeight:700 }}>Empresa Emitente</label>
                <select value={empresa?.empcod ?? ''} onChange={e => setEmpresa(empresas.find(x => x.empcod === Number(e.target.value)) ?? null)} style={{ fontWeight:600 }}>
                  {empresas.map(emp => <option key={emp.empcod} value={emp.empcod}>{emp.empcod} - {emp.empfan || emp.empnom}</option>)}
                </select>
              </div>
              <div className="field" style={{ flex:'0 0 155px', marginBottom:0 }}>
                <label style={{ color:'#bfdbfe', fontWeight:700 }}>Data Emissão</label>
                <input type="date" value={dataEmissao} onChange={e => setDataEmissao(e.target.value)} style={{ fontWeight:600 }} />
              </div>
              <div className="field" style={{ flex:'0 0 70px', marginBottom:0 }}>
                <label style={{ color:'#bfdbfe', fontWeight:700 }}>Série{carregandoNum && <span style={{ color:'#93c5fd', fontWeight:400 }}> ...</span>}</label>
                <input type="text" value={serie} onChange={e => setSerie(e.target.value)} maxLength={3} disabled={carregandoNum} style={{ fontWeight:600 }} />
              </div>
              <div className="field" style={{ flex:'0 0 110px', marginBottom:0 }}>
                <label style={{ color:'#bfdbfe', fontWeight:700 }}>Número{carregandoNum && <span style={{ color:'#93c5fd', fontWeight:400 }}> ...</span>}</label>
                <input type="number" value={numero} onChange={e => setNumero(e.target.value)} disabled={carregandoNum} style={{ fontWeight:600 }} />
              </div>
            </div>

            <div style={{ display:'flex', gap:'0.75rem', flexWrap:'wrap', alignItems:'flex-start' }}>
              <div className="field" style={{ flex:'0 0 260px' }}>
                <label>Tipo de Operação</label>
                <select value={tipoOp} onChange={e => setTipoOp(e.target.value)}>
                  <option value="VEN">VEN - Venda de Produtos/Mercadoria</option>
                  <option value="RCS">RCS - Remessa p/ Conserto</option>
                  <option value="CPL">CPL - Complementar (ICMS / ICMS ST)</option>
                  <option value="ANT">ANT - Anulação Prest. Serviço de Transporte</option>
                  <option value="OUT">OUT - Outras Operações Não Especificadas</option>
                  <option value="DEV">DEV - Devolução / Retorno</option>
                </select>
              </div>
              {tipoOp === 'DEV' && (
                <div className="field" style={{ flex:'0 0 400px' }}>
                  <label>Chave da NF-e Referenciada {errosDados.includes('Chave') && <span style={{ color:'#dc2626' }}>*</span>}</label>
                  <input type="text" value={chaveRef} maxLength={44} placeholder="44 dígitos"
                    onChange={e => { setChaveRef(e.target.value.replace(/\D/g, '')); setErrosDados(p => p.filter(x => x !== 'Chave')) }}
                    style={{ fontFamily:'monospace', outline: errosDados.includes('Chave') ? '2px solid #dc2626' : 'none' }} />
                </div>
              )}
              <div className="field" style={{ flex:'0 0 160px' }}>
                <label>Operação</label>
                <select value={tpNF} onChange={e => setTpNF(e.target.value as any)}>
                  <option value="1">Saída</option>
                  <option value="0">Entrada</option>
                </select>
              </div>
              <div className="field" style={{ flex:'0 0 200px' }}>
                <label>Tipo Consumidor</label>
                <select value={indFinal} onChange={e => setIndFinal(e.target.value as any)}>
                  <option value="0">Normal</option>
                  <option value="1">Consumidor Final</option>
                </select>
              </div>
              <div className="field" style={{ flex:'0 0 280px' }}>
                <label>Presença {errosDados.includes('Presença') && <span style={{ color:'#dc2626' }}>*</span>}</label>
                <select value={indPres} onChange={e => { setIndPres(e.target.value); setErrosDados(p => p.filter(x => x !== 'Presença')) }}
                  style={{ outline: errosDados.includes('Presença') ? '2px solid #dc2626' : 'none' }}>
                  <option value="">Selecione</option>
                  <option value="0">Não se Aplica</option>
                  <option value="1">Presencial</option>
                  <option value="2">Internet</option>
                  <option value="3">Teleatendimento</option>
                  <option value="4">Entrega a Domicílio</option>
                  <option value="9">Outros</option>
                </select>
              </div>
            </div>

            <div style={{ display:'flex', gap:'0.75rem', flexWrap:'wrap', alignItems:'flex-start', marginTop:'0.25rem' }}>
              <div className="field" style={{ flex:'0 0 540px' }}>
                <label>Natureza de Operação {errosDados.includes('Natureza') && <span style={{ color:'#dc2626' }}>*</span>}</label>
                <div style={{ outline: errosDados.includes('Natureza') ? '2px solid #dc2626' : 'none', borderRadius:'0.45rem' }}>
                  <NaturezaSelect
                    naturezas={naturezas.filter(n => n.tno_csa === 'C')}
                    value={natureza}
                    onChange={v => { setNatureza(v); setErrosDados(p => p.filter(x => x !== 'Natureza')) }}
                  />
                </div>
              </div>
              <div className="field" style={{ flex:'0 0 420px' }}>
                <label>Chave NF-e</label>
                <input type="text" value={chaveNfe} readOnly style={{ background:'#eff6ff', border:'1.5px solid #93c5fd', color:'#0d2d4e', fontWeight:700, fontFamily:'monospace', fontSize:'0.78rem', letterSpacing:'0.04em', cursor:'default' }} />
              </div>
            </div>

            <div className="field" style={{ maxWidth:620 }}>
              <label>Informações Complementares</label>
              <textarea value={obs} onChange={e => setObs(e.target.value)} rows={2} style={{ resize:'vertical' }} />
            </div>

            <div className="etapa-acoes">
              <button className="btn btn-secondary" onClick={() => setEtapa(0)}>← Voltar</button>
              <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:'0.3rem' }}>
                {errosDados.length > 0 && (
                  <span style={{ fontSize:'0.75rem', color:'#dc2626', fontWeight:600 }}>Preencha: {errosDados.join(', ')}</span>
                )}
                <button className="btn btn-primary" onClick={() => {
                  const erros: string[] = []
                  if (!natureza) erros.push('Natureza')
                  if (!indPres)  erros.push('Presença')
                  if (tipoOp === 'DEV' && chaveRef.length !== 44) erros.push('Chave')
                  if (!numero)   erros.push('Número')
                  setErrosDados(erros)
                  if (erros.length === 0) setEtapa(2)
                }}>Próximo →</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Etapa 2: Itens ───────────────────────────────────────────────── */}
        {etapa === 2 && (
          <div>
            <h3 className="etapa-titulo">Itens / Produtos</h3>
            <table>
              <thead>
                <tr>
                  <th>Produto</th>
                  <th style={{ width:'8%' }}>Qtd</th>
                  <th style={{ width:'15%' }}>Vl. Unit.</th>
                  <th style={{ width:'13%' }}>Total</th>
                  <th style={{ width:'8%' }}></th>
                </tr>
              </thead>
              <tbody>
                {itens.map((it, i) => (
                  <React.Fragment key={i}>
                    <tr>
                      <td style={{ padding:'0.35rem 0.5rem' }}>
                        <ItemSelect somenteAtivos
                          codValue={it.cProd}
                          descValue={it.descricao}
                          unValue={it.unidade}
                          onChange={(cod, des, un, _precod, ncm, cest) => {
                            setItens(prev => prev.map((x, idx) =>
                              idx === i ? {
                                ...x,
                                cProd: cod,
                                descricao: des,
                                unidade: un,
                                NCM:  ncm?.replace(/\D/g, '') || x.NCM,
                                CEST: cest?.replace(/\D/g, '') || x.CEST,
                              } : x
                            ))
                            if (cod) setItemExpandido(i)
                          }}
                        />
                      </td>
                      <td><input type="number" min={0} value={it.quantidade} onChange={e => updateItem(i, 'quantidade', Number(e.target.value))} /></td>
                      <td><input type="number" min={0} step={0.01} value={it.valorUnitario} onChange={e => updateItem(i, 'valorUnitario', Number(e.target.value))} /></td>
                      <td style={{ fontWeight:600 }}>{(it.quantidade * it.valorUnitario).toLocaleString('pt-BR', { style:'currency', currency:'BRL' })}</td>
                      <td>
                        <div style={{ display:'flex', gap:'0.25rem', justifyContent:'center' }}>
                          <button className="btn btn-secondary btn-sm" title="Dados fiscais" onClick={() => setItemExpandido(itemExpandido === i ? null : i)}>
                            {itemExpandido === i ? '▴' : '▾'}
                          </button>
                          {itens.length > 1 && <button className="btn btn-danger btn-sm" onClick={() => removeItem(i)}>✕</button>}
                        </div>
                      </td>
                    </tr>

                    {/* Painel expandido por item — igual CT-e OS */}
                    {itemExpandido === i && (
                      <tr>
                        <td colSpan={5} style={{ background:'#f8fafc', padding:'0.6rem 1rem 1rem', borderTop:'1px dashed #e5e7eb' }}>
                          {/* NCM / CEST / Complemento */}
                          <div style={{ display:'grid', gridTemplateColumns:'120px 100px 1fr', gap:'0.6rem', alignItems:'end', marginBottom:'0.75rem' }}>
                            <div className="field" style={{ marginBottom:0 }}>
                              <label>NCM *</label>
                              <input value={it.NCM} maxLength={8} placeholder="00000000"
                                onChange={e => updateItem(i, 'NCM', e.target.value.replace(/\D/g, ''))} />
                            </div>
                            <div className="field" style={{ marginBottom:0 }}>
                              <label>CEST</label>
                              <input value={it.CEST} maxLength={7} placeholder="0000000"
                                onChange={e => updateItem(i, 'CEST', e.target.value.replace(/\D/g, ''))} />
                            </div>
                            <div className="field" style={{ marginBottom:0 }}>
                              <label>Complemento / Informação adicional</label>
                              <input type="text" value={it.complemento} maxLength={500}
                                placeholder="Ex: USADO-PLACA ISZ1667 / CHASSI-9532L82W7CR233501"
                                onChange={e => updateItem(i, 'complemento', e.target.value)} />
                            </div>
                          </div>

                          {/* Regra IBS/CBS */}
                          <div className="field" style={{ marginBottom:'0.6rem' }}>
                            <label>IBS/CBS (Regra)</label>
                            <IbsCbsSelect
                              codValue={it.ibsCbsRegra}
                              desValue={it.ibsCbsDes}
                              onChange={async (cod, des) => {
                                const total = it.quantidade * it.valorUnitario
                                let ibsCbs = { ...ibsCbsVazio }
                                if (cod) {
                                  try {
                                    const r = await api.get(`/erp/ibscbs/${cod}`)
                                    if (r.data) ibsCbs = calcIbsCbs(total, r.data)
                                  } catch {}
                                }
                                setItens(prev => prev.map((x, idx) =>
                                  idx === i ? { ...x, ibsCbsRegra: cod, ibsCbsDes: des, ibsCbs } : x
                                ))
                              }}
                            />
                          </div>

                          {/* Abas fiscais — IMPOSTOS | IBS/CBS */}
                          <div className="fiscal-tabs">
                            <button type="button"
                              className={`fiscal-tab${abaItem(i) === 'impostos' ? ' fiscal-tab--ativa' : ''}`}
                              onClick={() => setAbaFiscal(p => ({ ...p, [i]: 'impostos' }))}>Impostos</button>
                            <button type="button"
                              className={`fiscal-tab${abaItem(i) === 'ibscbs' ? ' fiscal-tab--ativa' : ''}`}
                              onClick={() => setAbaFiscal(p => ({ ...p, [i]: 'ibscbs' }))}>IBS/CBS</button>
                          </div>

                          {abaItem(i) === 'impostos' && (
                            <table className="imp-table">
                              <thead>
                                <tr>
                                  <th style={{ width:70 }}>Tributo</th>
                                  <th style={{ width:125 }}>Base Cálculo</th>
                                  <th style={{ width:75 }}>Alíquota</th>
                                  <th style={{ width:125 }}>Destaque</th>
                                  <th style={{ width:60 }}>CST</th>
                                </tr>
                              </thead>
                              <tbody>
                                {[
                                  { label:'ICMS',    bc:'icmsBase',   aliq:'icmsAliq',   dest:'icmsDest',   cst:'icmsCst'   },
                                  { label:'ICMS ST', bc:'stBase',     aliq:'stAliq',     dest:'stDest',     cst:null        },
                                  { label:'IPI',     bc:'ipiBase',    aliq:'ipiAliq',    dest:'ipiDest',    cst:'ipiCst'    },
                                  { label:'PIS',     bc:'pisBase',    aliq:'pisAliq',    dest:'pisDest',    cst:'pisCst'    },
                                  { label:'COFINS',  bc:'cofinsBase', aliq:'cofinsAliq', dest:'cofinsDest', cst:'cofinsCst' },
                                ].map(row => (
                                  <tr key={row.label}>
                                    <td className="imp-label">{row.label}</td>
                                    <td><input type="number" className="imp-input" min={0} step={0.01}
                                      value={f2((it as any)[row.bc])}
                                      onChange={e => updateItem(i, row.bc as keyof ItemNFe, Number(e.target.value))} /></td>
                                    <td><input type="number" className="imp-input imp-aliq" min={0} step={0.001}
                                      value={f2((it as any)[row.aliq])}
                                      onChange={e => updateItem(i, row.aliq as keyof ItemNFe, Number(e.target.value))} /></td>
                                    <td><input type="number" className="imp-input" min={0} step={0.01}
                                      value={f2((it as any)[row.dest])}
                                      onChange={e => updateItem(i, row.dest as keyof ItemNFe, Number(e.target.value))} /></td>
                                    <td>{row.cst
                                      ? <input type="text" className="imp-input imp-cst"
                                          value={(it as any)[row.cst]}
                                          onChange={e => updateItem(i, row.cst as keyof ItemNFe, e.target.value)} />
                                      : <span className="imp-vazio">—</span>}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}

                          {abaItem(i) === 'ibscbs' && (
                            <div>
                              <table className="imp-table" style={{ marginTop:'0.35rem' }}>
                                <thead>
                                  <tr>
                                    <th style={{ width:60 }}>Tributo</th>
                                    <th style={{ width:55 }}>CST</th>
                                    <th style={{ width:110 }}>Base Cálculo</th>
                                    <th style={{ width:115 }}>BC Efetiva</th>
                                    <th style={{ width:75 }}>Ali. Ref %</th>
                                    <th style={{ width:75 }}>Red. Ali %</th>
                                    <th style={{ width:75 }}>Ali. Efet %</th>
                                    <th style={{ width:110 }}>Valor</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td className="imp-label">CBS</td>
                                    <td style={{ textAlign:'center', fontSize:'0.78rem' }}>{it.ibsCbs.cst}</td>
                                    <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.cbsBc)} /></td>
                                    <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.cbsBcEf)} /></td>
                                    <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.cbsAliRef)} /></td>
                                    <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.cbsRedAli)} /></td>
                                    <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.cbsAliEf)} /></td>
                                    <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.cbsValor)} style={{ fontWeight:800, color:'#1a6ea8' }} /></td>
                                  </tr>
                                  <tr>
                                    <td className="imp-label">IBS UF</td>
                                    <td style={{ textAlign:'center', fontSize:'0.78rem', color:'#6b7280' }}>{it.ibsCbs.ibsUfEst}</td>
                                    <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.cbsBc)} /></td>
                                    <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.ibsUfBcEf)} /></td>
                                    <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.ibsUfAliRef)} /></td>
                                    <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.ibsUfRedAli)} /></td>
                                    <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.ibsUfAliEf)} /></td>
                                    <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.ibsUfValor)} style={{ fontWeight:800, color:'#1a6ea8' }} /></td>
                                  </tr>
                                  <tr>
                                    <td className="imp-label">IBS MUN</td>
                                    <td></td>
                                    <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.cbsBc)} /></td>
                                    <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.ibsMunBcEf)} /></td>
                                    <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.ibsMunAliRef)} /></td>
                                    <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.ibsMunRedAli)} /></td>
                                    <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.ibsMunAliEf)} /></td>
                                    <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.ibsMunValor)} style={{ fontWeight:800, color:'#1a6ea8' }} /></td>
                                  </tr>
                                  <tr>
                                    <td className="imp-label">IS</td>
                                    <td></td>
                                    <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.isSbas)} /></td>
                                    <td colSpan={3}></td>
                                    <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.isSali)} /></td>
                                    <td></td>
                                  </tr>
                                </tbody>
                              </table>
                              {it.ibsCbs.classTrib && (
                                <div style={{ marginTop:'0.35rem', fontSize:'0.75rem', color:'#6b7280' }}>
                                  <strong>Class. Trib.:</strong> {it.ibsCbs.classTrib} — {it.ibsCbs.classTribDes}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'0.75rem' }}>
              <button className="btn btn-secondary btn-sm" onClick={addItem}>+ Item</button>
              <div style={{ fontWeight:700, fontSize:'1rem', color:'#1a6ea8' }}>
                Total: {totalGeral.toLocaleString('pt-BR', { style:'currency', currency:'BRL' })}
              </div>
            </div>

            <div className="etapa-acoes">
              <button className="btn btn-secondary" onClick={() => setEtapa(1)}>← Voltar</button>
              <button className="btn btn-primary" onClick={() => {
                const semDesc = itens.some(it => !it.descricao?.trim())
                if (semDesc) { alert('Todos os itens precisam ter descrição.'); return }
                const semNcm = itens.some(it => !it.NCM || it.NCM.replace(/\D/g, '').length < 8)
                if (semNcm) { alert('Todos os itens precisam ter NCM com 8 dígitos.'); return }
                const semIbsCbs = itens.some(it => !it.ibsCbsRegra?.trim())
                if (semIbsCbs) { alert('Todos os itens precisam ter o Cód. IBS/CBS informado.'); return }
                // CST/cClassTrib em branco com a Regra já preenchida é sinal de que o
                // cálculo não rodou (corrida entre digitar o valor e escolher a regra) —
                // emitir() recalcula de novo antes de montar o payload, mas barra aqui
                // já avisa o usuário em vez de deixar pra descobrir só depois de gerar o XML.
                const ibsCbsSemCst = itens.some(it => it.ibsCbsRegra?.trim() && !it.ibsCbs.cst?.trim())
                if (ibsCbsSemCst) { alert('O IBS/CBS de algum item não calculou (CST em branco) — troque a Regra do item de novo pra recalcular.'); return }
                setEtapa(3)
              }}>Próximo →</button>
            </div>
          </div>
        )}

        {/* ── Etapa 3: Pagamento ───────────────────────────────────────────── */}
        {etapa === 3 && (() => {
          const brl = (v: number) => v.toLocaleString('pt-BR', { style:'currency', currency:'BRL' })
          const secStyle: React.CSSProperties = { marginBottom:'0.75rem', background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:'0.5rem', padding:'0.75rem 1rem' }
          const labelStyle: React.CSSProperties = { fontSize:'0.7rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'#6b7280', marginBottom:'0.4rem' }
          const rowStyle: React.CSSProperties = { display:'flex', justifyContent:'space-between', fontSize:'0.82rem', padding:'0.18rem 0', borderBottom:'1px dashed #e5e7eb' }

          const totalIcms   = itens.reduce((s, it) => s + (it.icmsDest   || 0), 0)
          const totalSt     = itens.reduce((s, it) => s + (it.stDest     || 0), 0)
          const totalIpi    = itens.reduce((s, it) => s + (it.ipiDest    || 0), 0)
          const totalPis    = itens.reduce((s, it) => s + (it.pisDest    || 0), 0)
          const totalCofins = itens.reduce((s, it) => s + (it.cofinsDest || 0), 0)
          const totalCbs    = itens.reduce((s, it) => s + (it.ibsCbs.cbsValor   || 0), 0)
          const totalIbsUf  = itens.reduce((s, it) => s + (it.ibsCbs.ibsUfValor || 0), 0)
          const totalIbsMun = itens.reduce((s, it) => s + (it.ibsCbs.ibsMunValor|| 0), 0)

          const impostosList = [
            { label:'ICMS',    valor: totalIcms   },
            { label:'ICMS ST', valor: totalSt     },
            { label:'IPI',     valor: totalIpi    },
            { label:'PIS',     valor: totalPis    },
            { label:'COFINS',  valor: totalCofins },
          ].filter(x => x.valor > 0)

          const ibscbsList = [
            { label:'CBS',     valor: totalCbs    },
            { label:'IBS UF',  valor: totalIbsUf  },
            { label:'IBS MUN', valor: totalIbsMun },
          ].filter(x => x.valor > 0)

          return (
          <div>
            <h3 className="etapa-titulo">Resumo</h3>

            {/* Cabeçalho: cliente + documento + total */}
            <div style={{ display:'flex', gap:'0.75rem', flexWrap:'wrap', marginBottom:'0.75rem' }}>
              <div style={{ ...secStyle, flex:'1 1 220px', marginBottom:0 }}>
                <div style={labelStyle}>Destinatário</div>
                <div style={{ fontWeight:700, fontSize:'0.9rem' }}>{cliente?.cli_nom}</div>
                <div style={{ fontSize:'0.78rem', color:'#6b7280' }}>{cliente ? formatDoc(cliente.cli_mat) : ''}</div>
                {cliente?.cli_ins?.trim() && <div style={{ fontSize:'0.75rem', color:'#6b7280' }}>IE: {cliente.cli_ins}</div>}
                {endereco?.logradouro?.trim() && <div style={{ fontSize:'0.75rem', color:'#6b7280' }}>{endereco.logradouro}, {endereco.numero} — {endereco.cidade}/{endereco.uf}</div>}
              </div>
              <div style={{ ...secStyle, flex:'1 1 220px', marginBottom:0 }}>
                <div style={labelStyle}>Documento</div>
                <div style={{ fontWeight:700, fontSize:'0.9rem' }}>NF-e · Série {serie} · Nº {numero}</div>
                <div style={{ fontSize:'0.78rem', color:'#6b7280' }}>Emissão: {dataEmissao}</div>
                <div style={{ fontSize:'0.78rem', color:'#6b7280' }}>CFOP: {nat?.tno_cdf} — {nat?.tno_des}</div>
                <div style={{ fontSize:'0.78rem', color:'#6b7280' }}>Nat. Op.: {nat?.tno_nat}</div>
                {chaveNfe && <div style={{ fontSize:'0.68rem', color:'#9ca3af', fontFamily:'monospace', marginTop:'0.2rem', wordBreak:'break-all' }}>{chaveNfe}</div>}
              </div>
              <div style={{ ...secStyle, flex:'0 0 auto', textAlign:'center', marginBottom:0 }}>
                <div style={labelStyle}>Total da Nota</div>
                <div style={{ fontWeight:800, fontSize:'1.4rem', color:'#f07820' }}>{brl(totalGeral)}</div>
                <div style={{ fontSize:'0.78rem', color:'#6b7280' }}>{itens.length} item(s)</div>
              </div>
            </div>

            {tipoOp === 'DEV' && (
              <div style={{ ...secStyle, borderLeft:'4px solid #7c3aed' }}>
                <div style={labelStyle}>NF-e Referenciada (Devolução / Retorno)</div>
                <div style={{ fontWeight:700, fontSize:'0.9rem' }}>
                  Série {notaRef?.serie || chaveRef.slice(22, 25)} · Nº {Number(notaRef?.numero ?? chaveRef.slice(25, 34))}
                  {notaRef?.data && <span style={{ fontWeight:400, color:'#6b7280' }}> · Emissão {notaRef.data.split('-').reverse().join('/')}</span>}
                </div>
                {notaRef?.nome && <div style={{ fontSize:'0.78rem', color:'#374151' }}>{notaRef.nome}{notaRef.doc ? ` — ${formatDoc(notaRef.doc)}` : ''}</div>}
                {!notaRef?.nome && notaRef?.doc && <div style={{ fontSize:'0.78rem', color:'#374151' }}>{formatDoc(notaRef.doc)}</div>}
                {notaRef && (
                  <div style={{ fontSize:'0.78rem', color:'#6b7280' }}>
                    {notaRef.valor != null && <>Valor: {brl(Number(notaRef.valor))}</>}
                    {notaRef.cfop && <> · CFOP {notaRef.cfop}</>}
                    {notaRef.natureza && <> · {notaRef.natureza}</>}
                    {notaRef.situacao && <> · {notaRef.situacao}</>}
                  </div>
                )}
                {notaRefErro && <div style={{ fontSize:'0.78rem', color:'#b45309' }}>{notaRefErro} — dados tirados da chave (emitente {formatDoc(chaveRef.slice(6, 20))}, {chaveRef.slice(4, 6)}/20{chaveRef.slice(2, 4)})</div>}
                <div style={{ fontSize:'0.68rem', color:'#9ca3af', fontFamily:'monospace', marginTop:'0.2rem', wordBreak:'break-all' }}>{chaveRef}</div>
              </div>
            )}

            {/* Itens */}
            <div style={secStyle}>
              <div style={labelStyle}>Itens</div>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.78rem' }}>
                <thead>
                  <tr className="thead-claro" style={{ background:'#f3f4f6' }}>
                    <th style={{ textAlign:'left', padding:'0.3rem 0.4rem', fontWeight:600 }}>Descrição</th>
                    <th style={{ textAlign:'center', padding:'0.3rem 0.4rem', fontWeight:600, width:70 }}>NCM</th>
                    <th style={{ textAlign:'center', padding:'0.3rem 0.4rem', fontWeight:600, width:50 }}>CFOP</th>
                    <th style={{ textAlign:'center', padding:'0.3rem 0.4rem', fontWeight:600, width:38 }}>CST</th>
                    <th style={{ textAlign:'center', padding:'0.3rem 0.4rem', fontWeight:600, width:80 }}>IBS/CBS</th>
                    <th style={{ textAlign:'center', padding:'0.3rem 0.4rem', fontWeight:600, width:35 }}>Qtd</th>
                    <th style={{ textAlign:'right', padding:'0.3rem 0.4rem', fontWeight:600, width:90 }}>Vl. Unit.</th>
                    <th style={{ textAlign:'right', padding:'0.3rem 0.4rem', fontWeight:600, width:90 }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {itens.map((it, i) => (
                    <tr key={i} style={{ borderBottom:'1px solid #f3f4f6' }}>
                      <td style={{ padding:'0.3rem 0.4rem' }}>
                        {it.descricao}
                        {it.complemento && <span style={{ color:'#9ca3af', fontSize:'0.72rem' }}> — {it.complemento}</span>}
                      </td>
                      <td style={{ textAlign:'center', padding:'0.3rem 0.4rem', color:'#6b7280', fontSize:'0.72rem' }}>{it.NCM}</td>
                      <td style={{ textAlign:'center', padding:'0.3rem 0.4rem', color:'#374151' }}>{nat?.tno_cdf}</td>
                      <td style={{ textAlign:'center', padding:'0.3rem 0.4rem', color:'#374151' }}>{it.icmsCst}</td>
                      <td style={{ textAlign:'center', padding:'0.3rem 0.4rem', color:'#1e40af', fontSize:'0.72rem' }}>
                        {it.ibsCbsRegra ? <span title={it.ibsCbsDes}>{it.ibsCbsRegra}</span> : <span style={{ color:'#ef4444' }}>—</span>}
                      </td>
                      <td style={{ textAlign:'center', padding:'0.3rem 0.4rem' }}>{it.quantidade}</td>
                      <td style={{ textAlign:'right', padding:'0.3rem 0.4rem' }}>{brl(it.valorUnitario)}</td>
                      <td style={{ textAlign:'right', padding:'0.3rem 0.4rem', fontWeight:600 }}>{brl(it.quantidade * it.valorUnitario)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Impostos + IBS/CBS */}
            <div style={{ display:'flex', gap:'0.75rem', flexWrap:'wrap', marginBottom:'0.75rem' }}>
              {impostosList.length > 0 && (
                <div style={{ ...secStyle, flex:'1 1 180px', marginBottom:0 }}>
                  <div style={labelStyle}>Impostos</div>
                  {impostosList.map(imp => (
                    <div key={imp.label} style={rowStyle}>
                      <span style={{ fontWeight:600 }}>{imp.label}</span>
                      <span style={{ color:'#dc2626', fontWeight:700 }}>{brl(imp.valor)}</span>
                    </div>
                  ))}
                </div>
              )}
              {ibscbsList.length > 0 && (
                <div style={{ ...secStyle, flex:'1 1 180px', marginBottom:0 }}>
                  <div style={labelStyle}>IBS / CBS</div>
                  {ibscbsList.map(imp => (
                    <div key={imp.label} style={rowStyle}>
                      <span style={{ fontWeight:600 }}>{imp.label}</span>
                      <span style={{ color:'#1e40af', fontWeight:700 }}>{brl(imp.valor)}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* Detalhes IBS/CBS por item */}
              {itens.some(it => it.ibsCbsRegra) && (
                <div style={{ ...secStyle, flex:'2 1 280px', marginBottom:0 }}>
                  <div style={labelStyle}>Detalhes IBS/CBS por item</div>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.75rem' }}>
                    <thead>
                      <tr className="thead-claro" style={{ background:'#f3f4f6' }}>
                        <th style={{ textAlign:'left', padding:'0.2rem 0.4rem', fontWeight:600 }}>Produto</th>
                        <th style={{ textAlign:'center', padding:'0.2rem 0.4rem', fontWeight:600 }}>CST</th>
                        <th style={{ textAlign:'center', padding:'0.2rem 0.4rem', fontWeight:600 }}>Classe</th>
                        <th style={{ textAlign:'right', padding:'0.2rem 0.4rem', fontWeight:600 }}>CBS</th>
                        <th style={{ textAlign:'right', padding:'0.2rem 0.4rem', fontWeight:600 }}>IBS UF</th>
                        <th style={{ textAlign:'right', padding:'0.2rem 0.4rem', fontWeight:600 }}>IBS Mun</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itens.map((it, i) => (
                        <tr key={i} style={{ borderBottom:'1px solid #f3f4f6' }}>
                          <td style={{ padding:'0.2rem 0.4rem', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={it.descricao}>{it.descricao}</td>
                          <td style={{ textAlign:'center', padding:'0.2rem 0.4rem', color:'#374151' }}>{it.ibsCbs.cst || '—'}</td>
                          <td style={{ textAlign:'center', padding:'0.2rem 0.4rem', color:'#6b7280' }}>{it.ibsCbs.classTrib || '—'}</td>
                          <td style={{ textAlign:'right', padding:'0.2rem 0.4rem', color:'#1e40af', fontWeight:600 }}>{brl(it.ibsCbs.cbsValor)}</td>
                          <td style={{ textAlign:'right', padding:'0.2rem 0.4rem', color:'#1e40af', fontWeight:600 }}>{brl(it.ibsCbs.ibsUfValor)}</td>
                          <td style={{ textAlign:'right', padding:'0.2rem 0.4rem', color:'#1e40af', fontWeight:600 }}>{brl(it.ibsCbs.ibsMunValor)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {obs.trim() && (
              <div style={secStyle}>
                <div style={labelStyle}>Informações Complementares</div>
                <div style={{ fontSize:'0.82rem', whiteSpace:'pre-wrap' }}>{obs}</div>
              </div>
            )}

            {erro && <div className="login-error" style={{ marginTop:'0.5rem' }}>{erro}</div>}

            <div className="etapa-acoes">
              <button className="btn btn-secondary" onClick={() => setEtapa(2)}>← Voltar</button>
              <button type="button"
                onClick={() => {
                  setFinParcelasAntesModal(finParcelas)
                  if (finParcelas.length === 0) {
                    const base = new Date(dataEmissao)
                    setFinParcelas(Array.from({ length: finQtdParcelas }, (_, i) => {
                      const d = new Date(base)
                      d.setDate(d.getDate() + finIntervalo * (i + 1))
                      return { numero: String(i + 1), vencimento: d.toISOString().slice(0, 10), valor: (totalGeral / finQtdParcelas).toFixed(2) }
                    }))
                  }
                  setFinanceiroModal(true)
                }}
                style={{ background:'#0f766e', color:'#fff', border:'none', borderRadius:6, padding:'0.35rem 0.9rem', fontSize:'0.82rem', fontWeight:600, cursor:'pointer' }}>
                {finParcelas.length > 0 ? 'Financeiro ✓' : '+ Financeiro'}
              </button>
              <button className="btn btn-primary" onClick={() => { setEtapa(4); emitir() }} disabled={enviando || cliente?.bloqueado}>
                {enviando ? 'Transmitindo...' : editId ? 'Salvar e Transmitir →' : 'Emitir NF-e →'}
              </button>
            </div>
          </div>
          )
        })()}

      </div>

    {/* ── Modal Financeiro ── */}
    {financeiroModal && (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'flex-start', justifyContent:'center', zIndex:1000, padding:'24px 12px' }}
        onClick={fecharFinanceiroSemSalvar}>
        <div style={{ background:'#fff', borderRadius:8, width:560, maxWidth:'96vw', maxHeight:'calc(100vh - 48px)', overflow:'hidden', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}
          onClick={e => e.stopPropagation()}>
          <div style={{ background:'#0f766e', color:'#fff', padding:'0.85rem 1.25rem', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <div style={{ fontWeight:700, fontSize:'1rem' }}>Financeiro</div>
              <div style={{ fontSize:'0.78rem', opacity:0.85, marginTop:2 }}>
                Série {serie} · Nº {numero} — Total: {totalGeral.toLocaleString('pt-BR', { style:'currency', currency:'BRL' })}
              </div>
              {finCondDes && (
                <div style={{ fontSize:'0.75rem', opacity:0.85, marginTop:2 }}>
                  Condição de Pagamento: {finCondDes}
                </div>
              )}
            </div>
            <button onClick={fecharFinanceiroSemSalvar} style={{ background:'rgba(255,255,255,0.18)', border:'none', cursor:'pointer', color:'#fff', padding:'2px 8px', borderRadius:4, fontSize:'1rem' }}>✕</button>
          </div>
          <div style={{ overflowY:'auto', flex:1, padding:'1rem 1.25rem' }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem', marginBottom:'1rem' }}>
              <div>
                <label style={{ fontSize:'0.75rem', fontWeight:600, color:'#374151', display:'block', marginBottom:3 }}>Qtd. Parcelas</label>
                <input type="number" min={1} max={36} value={finQtdParcelas}
                  onChange={e => {
                    const qtd = Math.max(1, Math.min(36, Number(e.target.value)))
                    setFinQtdParcelas(qtd)
                    const base = new Date(dataEmissao)
                    setFinParcelas(Array.from({ length: qtd }, (_, i) => {
                      const d = new Date(base)
                      d.setDate(d.getDate() + finIntervalo * (i + 1))
                      return { numero: String(i + 1), vencimento: d.toISOString().slice(0, 10), valor: (totalGeral / qtd).toFixed(2) }
                    }))
                  }}
                  style={{ width:'100%', boxSizing:'border-box', border:'1px solid #d1d5db', borderRadius:4, padding:'6px 8px', fontSize:'0.85rem' }} />
              </div>
              <div>
                <label style={{ fontSize:'0.75rem', fontWeight:600, color:'#374151', display:'block', marginBottom:3 }}>Intervalo (dias)</label>
                <input type="number" min={0} value={finIntervalo}
                  onChange={e => {
                    const intervalo = Math.max(0, Number(e.target.value))
                    setFinIntervalo(intervalo)
                    const base = new Date(dataEmissao)
                    setFinParcelas(Array.from({ length: finQtdParcelas }, (_, i) => {
                      const d = new Date(base)
                      d.setDate(d.getDate() + intervalo * (i + 1))
                      return { numero: String(i + 1), vencimento: d.toISOString().slice(0, 10), valor: (totalGeral / finQtdParcelas).toFixed(2) }
                    }))
                  }}
                  style={{ width:'100%', boxSizing:'border-box', border:'1px solid #d1d5db', borderRadius:4, padding:'6px 8px', fontSize:'0.85rem' }} />
              </div>
            </div>
            <div style={{ marginBottom:'1rem' }}>
              <label style={{ fontSize:'0.75rem', fontWeight:600, color:'#374151', display:'block', marginBottom:3 }}>
                Conta Financeira <span style={{ color:'#ef4444' }}>*</span>
              </label>
              <div style={{ display:'flex', gap:'0.5rem', position:'relative' }}>
                <input type="text" value={finContaCod}
                  onChange={e => { setFinContaCod(e.target.value); setFinContaNome('') }}
                  onBlur={async e => {
                    const cod = e.target.value.trim()
                    if (!cod) return
                    const r = await api.get('/erp/contas', { params: { busca: cod } })
                    const found = r.data.find((c: any) => c.dep_cod.trim() === cod)
                    if (found) setFinContaNome(found.dep_des)
                  }}
                  placeholder="Código" autoComplete="off"
                  style={{ width:80, border:`1px solid ${finContaCod.trim() ? '#d1d5db' : '#ef4444'}`, borderRadius:4, padding:'6px 8px', fontSize:'0.85rem' }} />
                <div style={{ flex:1, position:'relative' }}>
                  {finContaNome ? (
                    <div style={{ display:'flex', alignItems:'center', border:'1px solid #6ee7b7', borderRadius:4, padding:'6px 8px', background:'#f0fdf4', fontSize:'0.85rem', gap:'0.5rem' }}>
                      <span style={{ flex:1, color:'#065f46', fontWeight:600 }}>{finContaNome}</span>
                      <button type="button" onClick={() => { setFinContaCod(''); setFinContaNome(''); setFinContaBusca('') }}
                        style={{ background:'none', border:'none', cursor:'pointer', color:'#6b7280', fontSize:'1rem', lineHeight:1, padding:0 }}>×</button>
                    </div>
                  ) : (
                    <>
                      <input type="text" value={finContaBusca}
                        onChange={async e => {
                          const v = e.target.value
                          setFinContaBusca(v); setFinContaCod('')
                          if (v.length >= 2) {
                            const r = await api.get('/erp/contas', { params: { busca: v } })
                            setFinContaResultados(r.data); setFinContaAberto(true)
                          } else { setFinContaResultados([]); setFinContaAberto(false) }
                        }}
                        onBlur={() => setTimeout(() => setFinContaAberto(false), 150)}
                        placeholder="Buscar nome da conta..." autoComplete="off"
                        style={{ width:'100%', boxSizing:'border-box', border:'1px solid #d1d5db', borderRadius:4, padding:'6px 8px', fontSize:'0.85rem' }} />
                      {finContaAberto && finContaResultados.length > 0 && (
                        <div style={{ position:'absolute', zIndex:50, background:'#fff', border:'1px solid #d1d5db', borderRadius:4, boxShadow:'0 4px 12px rgba(0,0,0,0.1)', width:'100%', maxHeight:200, overflowY:'auto', top:'100%', left:0 }}>
                          {finContaResultados.map(c => (
                            <div key={c.dep_cod} onMouseDown={() => { setFinContaCod(c.dep_cod); setFinContaNome(c.dep_des); setFinContaBusca(''); setFinContaAberto(false) }}
                              style={{ padding:'7px 10px', cursor:'pointer', fontSize:'0.82rem', borderBottom:'1px solid #f3f4f6' }}
                              onMouseEnter={e => (e.currentTarget.style.background = '#f0fdf4')}
                              onMouseLeave={e => (e.currentTarget.style.background = '')}>
                              <span style={{ fontFamily:'monospace', fontWeight:700, marginRight:8, color:'#374151' }}>{c.dep_cod}</span>
                              <span style={{ color:'#6b7280' }}>{c.dep_des}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            {finParcelas.length > 0 && (
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
                <thead>
                  <tr className="thead-claro" style={{ background:'#f9fafb' }}>
                    <th style={{ padding:'6px 8px', border:'1px solid #e5e7eb', textAlign:'center', width:60 }}>Parcela</th>
                    <th style={{ padding:'6px 8px', border:'1px solid #e5e7eb', textAlign:'left' }}>Vencimento</th>
                    <th style={{ padding:'6px 8px', border:'1px solid #e5e7eb', textAlign:'right' }}>Valor (R$)</th>
                  </tr>
                </thead>
                <tbody>
                  {finParcelas.map((p, i) => (
                    <tr key={i}>
                      <td style={{ padding:'5px 8px', border:'1px solid #e5e7eb', textAlign:'center', fontWeight:700, color:'#6b7280' }}>{p.numero}</td>
                      <td style={{ padding:'4px 8px', border:'1px solid #e5e7eb' }}>
                        <input type="date" value={p.vencimento}
                          onChange={e => setFinParcelas(prev => prev.map((x, j) => j === i ? { ...x, vencimento: e.target.value } : x))}
                          style={{ border:'none', background:'transparent', fontSize:'0.82rem', width:'100%' }} />
                      </td>
                      <td style={{ padding:'4px 8px', border:'1px solid #e5e7eb' }}>
                        <input type="number" value={p.valor}
                          onChange={e => setFinParcelas(prev => prev.map((x, j) => j === i ? { ...x, valor: e.target.value } : x))}
                          style={{ border:'none', background:'transparent', fontSize:'0.82rem', textAlign:'right', width:'100%' }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background:'#f9fafb', fontWeight:700 }}>
                    <td colSpan={2} style={{ padding:'6px 8px', border:'1px solid #e5e7eb', textAlign:'right' }}>Total</td>
                    <td style={{ padding:'6px 8px', border:'1px solid #e5e7eb', textAlign:'right', color:'#0f766e' }}>
                      {finParcelas.reduce((s, p) => s + Number(p.valor), 0).toLocaleString('pt-BR', { minimumFractionDigits:2 })}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
          <div style={{ borderTop:'1px solid #e5e7eb', padding:'0.65rem 1.25rem', display:'flex', gap:'0.5rem', justifyContent:'flex-end', background:'#f9fafb' }}>
            <button onClick={fecharFinanceiroSemSalvar} style={{ padding:'5px 16px', background:'#fff', border:'1px solid #d1d5db', borderRadius:4, fontSize:'0.82rem', cursor:'pointer', color:'#6b7280' }}>Cancelar</button>
            <button
              disabled={!finContaCod.trim()}
              onClick={() => setFinanceiroModal(false)}
              style={{ padding:'5px 18px', background: finContaCod.trim() ? '#0f766e' : '#9ca3af', color:'#fff', border:'none', borderRadius:4, fontSize:'0.82rem', fontWeight:600, cursor: finContaCod.trim() ? 'pointer' : 'not-allowed', opacity: finContaCod.trim() ? 1 : 0.7 }}>
              Confirmar
            </button>
          </div>
        </div>
      </div>
    )}
  </>
  )
}
